import cx from 'classnames';
import type { AriaRole, CSSProperties, FunctionComponent } from 'react';

import styles from './Link.module.less';

type LinkVariant = 'default' | 'primary' | 'text';

export type LinkProps = {
  to: string;
  openInNewTab?: boolean;
  testId?: string;
  isDisabled?: boolean;
  variant?: LinkVariant;
  /**
   * Optionally provide an ARIA role for the link.
   */
  role?: AriaRole;
  /**
   * Make the link focusable, and specify the tab order.
   */
  tabIndex?: number;
  /**
   * @deprecated - Use a variant instead. Add a new one if the existing ones don't suit your needs.
   */
  style?: CSSProperties;
  /**
   * @deprecated - Use a variant instead. Add a new one if the existing ones don't suit your needs.
   */
  className?: string;
  /**
   * Should the link prevent propagation of events
   */
  shouldStopPropagation?: boolean;
  /**
   * HTML title attribute
   */
  title?: string;
};

export const NEW_TAB_PROPS = {
  target: '_blank',
  rel: 'noopener noreferrer',
};

/**
 * A simple, generic navigational Link component.
 *
 * @param {LinkProps} props - Link props
 * @param {string} props.to - Where the link goes to.
 * @param {boolean} [props.openInNewTab] - If the link should open in a new tab.
 * @param {string} [props.className] - Custom class name.
 * @param {CSSProperties} [props.style] - Bring your own style!
 * @param {string} [props.testId] - Adds a `data-test-id` attribute, useful for selenium testing.
 * @param {string} [props.title] - HTML title attribute
 */
const Link: FunctionComponent<LinkProps> = ({
  to,
  children,
  openInNewTab = false,
  testId,
  isDisabled,
  variant = 'default',
  role,
  tabIndex,
  shouldStopPropagation,
  className,
  style,
  title,
}) => (
  <a
    role={role}
    className={cx(className, {
      [styles.variantDefault]: variant === 'default',
      [styles.variantPrimary]: variant === 'primary',
      [styles.variantText]: variant === 'text',
      [styles.disabled]: isDisabled,
    })}
    style={style}
    href={to}
    tabIndex={tabIndex}
    onClick={shouldStopPropagation ? (e) => e.stopPropagation() : undefined}
    title={title}
    {...(testId ? { 'data-test-id': testId } : {})}
    {...(openInNewTab ? NEW_TAB_PROPS : {})}
  >
    {children}
  </a>
);

export default Link;
